import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FormRow from "./FormRow";
import AvailabilityEditor from "./AvailabilityEditor";
import TimeOffEditor from "./TimeOffEditor";
import { timeOffOf, toIsoDate, upcoming } from "../utils/timeOff";
import { resourceFieldId } from "../utils/validation";
import {
  formControlClass,
  formControlErrorClass,
  formErrorTextClass,
} from "../../../dashboardStyles";

const TYPE_ICONS = {
  Person: "bi-person",
  Space: "bi-house-door",
  Other: "bi-three-dots",
};

/** Whether `email` is the signed-in account's own address. */
const isOwnAddress = (email, accountEmail) =>
  Boolean(email && accountEmail) &&
  email.trim().toLowerCase() === accountEmail.trim().toLowerCase();

/**
 * One resource on the team step: what it is, what it is called, how to reach a
 * person, and when it can be booked.
 *
 * The email is required for a person. Usually it is someone else's, and they
 * are sent an invitation to see their own bookings; when it is the signed-in
 * owner's own address, the server links the resource to their account instead
 * and sends nothing — the hint under the field says which is about to happen.
 */
const ResourceEditor = ({
  resource,
  resourceTypes,
  accountEmail,
  errors = {},
  onUpdate,
}) => {
  const { t } = useTranslation();
  const updateField = (field, value) => onUpdate(resource.id, field, value);

  const nameId = resourceFieldId("name", resource.id);
  const emailId = resourceFieldId("email", resource.id);
  const nameError = errors[nameId];
  const emailError = errors[emailId];
  const hasError = Object.keys(errors).some(
    (key) => key.endsWith(`-${resource.id}`) && errors[key],
  );
  const ownAddress = isOwnAddress(resource.email, accountEmail);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const scheduledTimeOff = upcoming(
    timeOffOf(resource.monthlySchedule),
    toIsoDate(new Date()),
  ).length;
  const timeOffPanelId = `time-off-panel-${resource.id}`;

  return (
    // The whole card turns red, not just the box: with several people on the
    // team the input alone is easy to scroll past.
    <div
      className={`rounded-card border bg-white p-4 sm:p-5 ${
        hasError ? "border-red-300 ring-2 ring-red-100" : "border-ink-200"
      }`}
      data-invalid={hasError ? "true" : undefined}
    >
      <div
        role="group"
        aria-label={t("Dashboard.Wizard.resourceItem.typeOfResource")}
        className="flex gap-1 rounded-lg border border-ink-200 bg-ink-50 p-1"
      >
        {resourceTypes.map((type) => {
          const active = resource.type === type;
          return (
            <button
              key={type}
              type="button"
              aria-pressed={active}
              onClick={() => updateField("type", type)}
              className={`inline-flex min-h-11 min-w-0 flex-1 basis-0 cursor-pointer items-center justify-center gap-1.5 rounded-md px-2 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                active
                  ? "border border-brand-600 bg-white text-brand-800 shadow-card"
                  : "border border-transparent text-ink-600 hover:text-ink-900"
              }`}
            >
              <i
                className={`bi ${TYPE_ICONS[type]} hidden sm:inline-block`}
                aria-hidden="true"
              />
              <span className="truncate">
                {t(`Dashboard.Wizard.resourceItem.types.${type}`)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col gap-4">
        <FormRow htmlFor={nameId} label={t("Dashboard.Setup.team.name")}>
          <input
            type="text"
            id={nameId}
            placeholder={t(
              resource.type === "Person"
                ? "Dashboard.Setup.team.namePlaceholderPerson"
                : "Dashboard.Setup.team.namePlaceholderOther",
            )}
            value={resource.name}
            onChange={(e) => updateField("name", e.target.value)}
            className={nameError ? formControlErrorClass : formControlClass}
            aria-invalid={nameError ? "true" : undefined}
            aria-describedby={nameError ? `${nameId}-error` : undefined}
          />
          {nameError && (
            <span id={`${nameId}-error`} className={formErrorTextClass}>
              {nameError}
            </span>
          )}
        </FormRow>

        {resource.type === "Person" && (
          <FormRow htmlFor={emailId} label={t("Dashboard.Wizard.resourceItem.email")}>
            <input
              type="email"
              id={emailId}
              required
              placeholder={t("Dashboard.Setup.team.emailPlaceholder")}
              value={resource.email || ""}
              onChange={(e) => updateField("email", e.target.value)}
              className={emailError ? formControlErrorClass : formControlClass}
              aria-invalid={emailError ? "true" : undefined}
              aria-describedby={
                emailError ? `${emailId}-error` : `${emailId}-hint`
              }
            />
            {/* The error replaces the hint: promising an invitation to an
                address that cannot receive one is the opposite of what this
                field needs to say. */}
            {emailError ? (
              <span id={`${emailId}-error`} className={formErrorTextClass}>
                {emailError}
              </span>
            ) : (
              <p id={`${emailId}-hint`} className="m-0 mt-1.5 text-xs text-ink-500">
                {ownAddress
                  ? t("Dashboard.Setup.team.selfHint")
                  : resource.email
                    ? t("Dashboard.Wizard.resourceItem.inviteHint")
                    : t("Dashboard.Setup.team.emailHint")}
              </p>
            )}
          </FormRow>
        )}

        <div className="border-t border-ink-100 pt-4">
          <AvailabilityEditor
            resource={resource}
            errors={errors}
            onUpdate={updateField}
          />

          {/* Leave and breaks sit with the week they are taken out of, folded
              away: most people set them later, from the Team page. */}
          <div className="mt-2 rounded-lg border border-ink-200">
            <button
              type="button"
              aria-expanded={timeOffOpen}
              aria-controls={timeOffPanelId}
              onClick={() => setTimeOffOpen((open) => !open)}
              className="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-ink-700 transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600"
            >
              <i className="bi bi-airplane" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block">{t("Dashboard.TimeOff.title")}</span>
                <span className="block text-xs text-ink-500">
                  {scheduledTimeOff > 0
                    ? t("Dashboard.TimeOff.scheduledCount", { count: scheduledTimeOff })
                    : t("Dashboard.TimeOff.nothingScheduled")}
                </span>
              </span>
              <i
                className={`bi bi-chevron-down text-xs transition-transform ${
                  timeOffOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {timeOffOpen && (
              <div id={timeOffPanelId} className="border-t border-ink-200 p-3">
                <TimeOffEditor
                  resource={resource}
                  idPrefix={`time-off-${resource.id}`}
                  onChange={(value) => updateField("monthlySchedule", value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ResourceEditor.propTypes = {
  resource: PropTypes.object.isRequired,
  resourceTypes: PropTypes.array.isRequired,
  /** The signed-in account's email, to tell "this is me" from an invitation. */
  accountEmail: PropTypes.string,
  /** The step's errors object; this card reads its own keys from it. */
  errors: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
};

export default ResourceEditor;
